import React, { useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  IconButton,
  InputAdornment,
  Paper,
  Popover,
  styled,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Tooltip,
  Avatar,
  Card,
  useMediaQuery,
  Typography,
  Grid,
  Autocomplete,
  Button,
} from '@mui/material';
import {
  EmojiEmotions,
  Send,
  AttachFile,
  Close,
  AddLink,
  FormatAlignLeft,
  EditNote,
  Attachment,
  KeyboardArrowUp,
  KeyboardArrowDown,
  Tune,
  AddLocationAlt,
  TravelExplore,
} from '@mui/icons-material';
import { useAddAnnouncementMutation, useEditAnnouncementMutation } from '../announcementApiSlice';
import toast from 'react-hot-toast';
import EmojiPicker from 'emoji-picker-react';
import 'react-quill/dist/quill.snow.css';
import { severityIcon } from '../utility/utilities';
import ReactQuill from 'react-quill';
import { quillFix } from '../../../utils/quillFix';
import AttachAvatar from '../../../common/AttachAvatar';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../context/Ability';
import { subject } from '@casl/ability';
import { useTheme } from '@emotion/react';
import { variants } from '../../../variants/variants';
import SwipeEdge from '../../../common/SwipeEdge';
import { toggleSwipe } from '../../Search/searchSlice';
import { useDispatch, useSelector } from 'react-redux';
import useDetectKeyboardOpen from 'use-detect-keyboard-open';
import { countries, regions } from '../../../constants/region';
import { selectPingsUploadProgress, selectPingsUploadStarted } from '../announcementSlice';
import { selectDeviceType } from '../../Auth/authSlice';

const StyledPaper = styled(Paper)({
  padding: '1rem 0.5rem 0.5rem 0.5rem',
  display: 'flex',
  alignItems: 'flex-end',
  background: 'transparent',
  flexDirection: 'column',
  minHeight: '60px',
  position: 'absolute',
  boxShadow: 'none',
  bottom: 15,
  left: 0,
  right: 0,
  zIndex: 100,

  // border: '1px solid black',
});

const QuillWrapper = styled(Box)(({ theme }) => ({
  border: '1px solid lightgrey',
  borderRadius: '20px',
  display: 'flex',
  alignItems: 'center',
  alignContent: 'center',
  width: '100%',
  // flex: 1,
  // width: `calc(85vw - 100px)`,
  position: 'relative',
  paddingLeft: '30px',
  [theme.breakpoints.down('sm')]: {
    paddingLeft: '15px !important',
  },
  background: 'white',
  [theme.breakpoints.down('lg')]: {
    // borderTopLeftRadius: 0,
    width: 'calc(100% - 40px)',
  },
}));

const StyledQuill = styled(ReactQuill)({
  '& .ql-container': {
    border: 'none',
    border: 0,
    backgroundColor: '#ffffff',
    '&.ql-focused': {
      boxShadow: '#3f51b5 0 0 0 2px',
    },
  },
  '& .ql-container.ql-snow': {
    height: 'auto',
    border: 0,
  },
  '& .ql-editor': {
    maxHeight: '150px',
    minHeight: '40px',
    overflowY: 'scroll',
    border: 0,
  },
  '& .ql-toolbar': {
    display: 'none',
  },
  '.ql-editor.ql-blank::before': {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  '&.show-toolbar .ql-toolbar': {
    display: 'block',
    border: 'none',
    background: 'white',
  },
  '.ql-tooltip': {
    bottom: 'auto !important',
    top: '-47px !important' /* Adjust the value as needed */,
    left: '0% !important',
    boxShadow: 'none',
    borderRadius: '8px',
    marginTop: '0% !important',
  },
  '.ql-tooltip > input': {
    padding: '5px 10px',
    maxWidth: 120,
    borderRadius: '8px',
    outlineColor: 'lightgray',
    outlineWidth: '0.2px',
    cursor: 'default',
    '&:hover': {
      backgroundColor: '#f1f1f1',
    },
  },
});

const validationSchema = Yup.object({
  announcement: Yup.string()
    .trim()
    .matches(/.*\S.*/, 'Announcement must contain at least one non-whitespace character.')
    .required('Announcement is required'),
  severity: Yup.string().required('Severity is required'),
  category: Yup.string().required('Category is required'),
  attachments: Yup.array().of(Yup.mixed()).max(4, 'You can upload up to 4 attachments.').notRequired(),
});

const Departments = [
  'Executive Leadership',
  'Technology & Engineering',
  'Quality Assurance',
  'Sales & Business Development',
  'Human Resources',
  'Finance & Accounting',
  'Marketing & Communications',
  'Legal & Compliance',
  'Operations & Administration',
  'Specialized Departments',
];

const AnnouncementForm = ({
  editingAnnouncement,
  setEditingAnnouncement,
  showScrollButton,
  onChange,
  inputRef,
  showToolbar,
  setShowToolbar,
}) => {
  const [addAnnouncement] = useAddAnnouncementMutation();
  const [editAnnouncement] = useEditAnnouncementMutation();
  const [filePreviews, setFilePreviews] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const quillRef = useRef(null);
  const ability = useAbility(AbilityContext);
  const open = Boolean(anchorEl);
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm')); // Detect if the screen is mobile
  const isKeyboardOpen = useDetectKeyboardOpen();
  const disable = Boolean();
  const uploadProgress = useSelector(selectPingsUploadProgress);
  const uploadStarted = useSelector(selectPingsUploadStarted);
  const os = useSelector(selectDeviceType);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      announcement: '',
      severity: 'minor',
      category: 'all',
      country: 'All',
      regions: ['All'],
      attachments: [],
    },
    validationSchema,
    onSubmit: async (values, { setFieldValue }) => {
      quillRef.current.getEditor().focus(); // Focus the Quill editor after submission

      const { announcement, severity, category, attachments, country, regions } = values;

      const slicedText = quillFix(announcement);
      console.log(slicedText);
      if (slicedText.length > 0 && slicedText.replaceAll('<br>', '').trim().length > 0) {
        const formData = new FormData();
        formData.append('announcementText', slicedText);
        formData.append('severity', severity);
        formData.append('category', category);
        formData.append('country', country);
        formData.append('regions', JSON.stringify(regions));
        if (attachments) {
          attachments.forEach((file) => {
            formData.append('attachments', file);
          });
        }
        const action = editingAnnouncement
          ? editAnnouncement({
              id: editingAnnouncement.announcementId,
              data: { announcementText: slicedText, severity, category, country, regions },
            })
          : addAnnouncement(formData);

        try {
          // await toast.promise(action.unwrap(), {
          //   loading: 'Processing announcement...',
          //   success: editingAnnouncement ? 'Announcement edited successfully!' : 'Announcement submitted successfully!',
          //   error: (err) => err.data?.message || 'Error processing announcement. Please try again.',
          // });
          await action.unwrap();
          setEditingAnnouncement(null);
          setFieldValue('announcement', '');
          setFieldValue('attachments', []);
          setFilePreviews([]);
          quillRef.current.getEditor().focus(); // Focus the Quill editor after submission
        } catch (error) {
          console.error('Error processing announcement:', error);
        }
      }
    },
  });

  useEffect(() => {
    if (editingAnnouncement) {
      formik.setValues({
        announcement: editingAnnouncement.announcementText,
        severity: editingAnnouncement.severity,
        category: editingAnnouncement.category,
        country: editingAnnouncement.country,
        regions: editingAnnouncement.regions,
      });
      if (quillRef.current) quillRef.current.getEditor().focus(); // Focus the Quill editor after submission
    }
  }, [editingAnnouncement]);

  const handleEmojiPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleEmojiClick = (emojiObject) => {
    const quillText = formik.values.announcement;
    const trimmedAnnouncement =
      quillText.startsWith('<p><br></p>') || !quillText.includes('</p><p><br></p>')
        ? quillText.replaceAll('<p><br></p>', '').trim()
        : quillText.replaceAll('<p><br></p>', '<br>').trim();

    const formattedText = trimmedAnnouncement.endsWith('</p>')
      ? trimmedAnnouncement.slice(0, trimmedAnnouncement.length - 4) + emojiObject.emoji + '</p>'
      : trimmedAnnouncement + emojiObject.emoji;
    formik.setFieldValue('announcement', formattedText);
    setAnchorEl(null);
  };

  const handleQuillChange = (content) => {
    formik.setFieldValue('announcement', content);
    onChange(inputRef.current.clientHeight);
  };
  const handleCancelEditing = () => {
    setEditingAnnouncement(null);
    formik.resetForm();
    setFilePreviews([]);
  };

  const handleAttachmentChange = (event) => {
    const files = Array.from(event.target.files);
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'video/mp4'];
    const validFiles = files.filter((file) => {
      return allowedTypes.includes(file.type);
    });
    const newAttachments = [...formik.values.attachments, ...validFiles].slice(0, 4);
    formik.setFieldValue('attachments', newAttachments);

    const previews = newAttachments.map((file) => {
      const fileType = file.type.split('/')[0];
      if (fileType === 'video') {
        return {
          type: 'video',
          url: URL.createObjectURL(file),
        };
      } else {
        return {
          type: 'image',
          url: URL.createObjectURL(file),
        };
      }
    });
    setFilePreviews(previews);
  };
  const handleRemoveAttachment = (index) => {
    const newAttachments = formik.values.attachments.filter((_, i) => i !== index);
    formik.setFieldValue('attachments', newAttachments);
    const newPreviews = filePreviews.filter((_, i) => i !== index);
    setFilePreviews(newPreviews);
  };
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        formik.handleSubmit();
      }
    },
    [formik]
  );
  // useEffect(() => {
  //   const quillEditor = quillRef.current.getEditor();
  //   const quillEditorInput = quillEditor.root;

  //   quillEditorInput.addEventListener('keydown', handleKeyDown);

  //   return () => {
  //     quillEditorInput.removeEventListener('keydown', handleKeyDown);
  //   };
  // }, [handleKeyDown]);

  const openConfig = () => {
    dispatch(toggleSwipe({ value: true }));
    window['ReactNativeWebView'] &&
      window['ReactNativeWebView']?.postMessage(JSON.stringify({ type: 'swipe', value: true }));
  };
  const [keepOpen, setKeepOpen] = useState(false);
  useEffect(() => {
    if (isKeyboardOpen) {
      setKeepOpen(true);
    } else {
      setKeepOpen(false);
    }
  }, [isKeyboardOpen]);
  // useLayoutEffect(() => {
  //   if (keepOpen && quillRef) {
  //     quillRef.current.getEditor().focus();
  //   }
  // }, [keepOpen, quillRef]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && keepOpen && quillRef.current) {
        quillRef.current.getEditor().focus(); // Refocus the Quill editor when the app becomes active
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [keepOpen]);
  useEffect(() => {
    //sending the progress status to webview
    window['ReactNativeWebView'] &&
      window['ReactNativeWebView']?.postMessage(
        JSON.stringify({ type: 'upload', progress: uploadProgress, uploadStarted: uploadStarted })
      );
  }, [uploadProgress, uploadStarted]);

  useEffect(() => {
    return () => {
      dispatch(toggleSwipe({ value: false }));
    };
  }, []);
  const SwipeContent = () => {
    const handlePingsSubmit = () => {
      formik.handleSubmit();
      dispatch(toggleSwipe({ value: false }));
      window['ReactNativeWebView'] &&
        window['ReactNativeWebView']?.postMessage(JSON.stringify({ type: 'swipe', value: false }));
    };
    return (
      <>
        <Box
          sx={{
            display: 'grid',
            // flexDirection: 'column',
            placeItems: 'center',
            alignContent: 'start',
            gap: 1,
            mt: 1,
            p: 2,
            backgroundColor: 'background.paper',
            borderRadius: '12px',
            overflow: 'auto',
            height: 'auto',
          }}
        >
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', mb: 2 }}>Who can see your Pings?</Typography>
          <FormControl fullWidth>
            <InputLabel id="category-label">Category *</InputLabel>
            <Select
              labelId="category-label"
              label="Category *"
              id="category"
              name="category"
              value={formik.values.category}
              onChange={formik.handleChange}
              sx={{
                borderRadius: '8px',
                backgroundColor: 'white',
              }}
            >
              {/* {Departments.map((department) => (
                ability.can('read', subject('Announcement', { category: department })) && (
                <MenuItem value='department'>{department}</MenuItem>
              )
              ))} */}
              {ability.can('read', subject('Announcement', { category: 'all' })) && (
                <MenuItem value="all">@all</MenuItem>
              )}
              {ability.can('read', subject('Announcement', { category: 'Executive Leadership' })) && (
                <MenuItem value="Executive Leadership">@Executive Leadership</MenuItem>
              )}
              {ability.can('read', subject('Announcement', { category: 'Technology & Engineering' })) && (
                <MenuItem value="Technology & Engineering">@Technology & Engineering</MenuItem>
              )}
              {ability.can('read', subject('Announcement', { category: 'Quality Assurance' })) && (
                <MenuItem value="Quality Assurance">@Quality Assurance</MenuItem>
              )}
              {ability.can('read', subject('Announcement', { category: 'Sales & Business Development' })) && (
                <MenuItem value="Sales & Business Development">@Sales & Business Development</MenuItem>
              )}
              {ability.can('read', subject('Announcement', { category: 'Human Resources' })) && (
                <MenuItem value="Human Resources">@Human Resources</MenuItem>
              )}
              {ability.can('read', subject('Announcement', { category: 'Finance & Accounting' })) && (
                <MenuItem value="Finance & Accounting">@Finance & Accounting</MenuItem>
              )}
              {ability.can('read', subject('Announcement', { category: 'Marketing & Communications' })) && (
                <MenuItem value="Marketing & Communications">@Marketing & Communications</MenuItem>
              )}
              {ability.can('read', subject('Announcement', { category: 'Legal & Compliance' })) && (
                <MenuItem value="Legal & Compliance">@Legal & Compliance</MenuItem>
              )}
              {ability.can('read', subject('Announcement', { category: 'Operations & Administration' })) && (
                <MenuItem value="Operations & Administration">@Operations & Administration</MenuItem>
              )}
              {ability.can('read', subject('Announcement', { category: 'Specialized Departments' })) && (
                <MenuItem value="Specialized Departments">@Specialized Departments</MenuItem>
              )}
              {/* {ability.can('read', subject('Announcement', { category: 'rmg' })) && (
                <MenuItem value='rmg'>@rmg</MenuItem>
              )}
              {ability.can('read', subject('Announcement', { category: 'manager' })) && (
                <MenuItem value='manager'>@manager</MenuItem>
              )}
              {ability.can('read', subject('Announcement', { category: 'hr' })) && <MenuItem value='hr'>@hr</MenuItem>}
              {ability.can('read', subject('Announcement', { category: 'rmg' })) && (
                <MenuItem value='rmg'>@rmg</MenuItem>
              )}
              {ability.can('read', subject('Announcement', { category: 'manager' })) && (
                <MenuItem value='manager'>@manager</MenuItem>
              )} */}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="severity-label">Severity *</InputLabel>
            <Select
              labelId="severity-label"
              id="severity"
              name="severity"
              label="Severity *"
              value={formik.values.severity}
              onChange={formik.handleChange}
              sx={{
                borderRadius: '8px',
                backgroundColor: 'white',
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center', // Align selected item content
                },
              }}
            >
              <MenuItem value="minor">Minor{severityIcon('minor')}</MenuItem>
              <MenuItem value="major">Major{severityIcon('major')}</MenuItem>
              <MenuItem value="critical">Critical{severityIcon('critical')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="country-label">Country *</InputLabel>
            <Select
              labelId="country-label"
              label="Country *"
              id="country"
              name="country"
              value={formik.values.country}
              onChange={(event) => {
                formik.setFieldValue('country', event.target.value);
                formik.setFieldValue('regions', event.target.value === 'All' ? ['All'] : []); // Reset regions when country changes
              }}
              sx={{
                borderRadius: '8px',
                backgroundColor: 'white',
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200, // Set the max height for the menu
                  },
                },
              }}
            >
              {countries.map((country) => (
                <MenuItem key={country} value={country}>
                  {country}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* <FormControl fullWidth>
            <Autocomplete
              options={formik.values.country ? regions[formik.values.country] : []}
              multiple
              limitTags={1}
              value={formik.values.regions}
              onChange={(event, newValue) => {
                formik.setFieldValue('regions', newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Region *'
                  variant='outlined'
                  disabled={!formik.values.country}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: 'off', // Disable browser autocomplete
                    readOnly: true, // Prevent input focus and keyboard opening
                    // onMouseDown: (event) => event.preventDefault(), // Prevent focus on click
                  }}
                />
              )}
              ListboxProps={{
                style: {
                  maxHeight: '280px',
                  overflow: 'auto',
                },
              }}
              sx={{
                borderRadius: '8px',
                backgroundColor: 'white',
                '& .MuiSvgIcon-root': {
                  color: 'black', // This applies the black color to all SVG icons
                },
              }}
              // onOpen={(event) => {
              //   event.target.blur(); // Ensure the input is not focused when opened
              // }}
            />
          </FormControl> */}
          <FormControl fullWidth>
            <InputLabel id="country-label">Region *</InputLabel>
            <Select
              labelId="region-label"
              label="Region *"
              id="region"
              name="region"
              multiple
              value={formik.values.regions}
              onChange={(event) => {
                formik.setFieldValue('regions', event.target.value);
              }}
              sx={{
                borderRadius: '8px',
                backgroundColor: 'white',
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200, // Set the max height for the menu
                  },
                },
              }}
            >
              {(formik.values.country ? regions[formik.values.country] : []).map((country) => (
                <MenuItem key={country} value={country}>
                  {country}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'grid', width: '100%', placeContent: 'end' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePingsSubmit}
              disabled={
                quillFix(formik.values.announcement).replaceAll('<br>', '').trim().length == 0 ||
                !formik.values.country ||
                formik.values.regions.length === 0
              }
              sx={{
                position: 'relative',
                borderRadius: 2,
                textTransform: 'none',
                alignItems: 'flex-end',
              }}
            >
              Ping
            </Button>
          </Box>
        </Box>
      </>
    );
  };

  return (
    <StyledPaper ref={inputRef} >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          openConfig(); // Open the swipe edge instead of direct submission
        }}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'flex-end',
          position: 'relative',
          height: '100%',
        
        }}
      >
        <QuillWrapper isKeyboardOpen={isKeyboardOpen}>
          <StyledQuill
            // onKeyDown={handleKeyDown}
            ref={quillRef}
            theme="snow"
            placeholder="Pings..."
            value={formik.values.announcement}
            onChange={(content) => handleQuillChange(content)}
            modules={{
              toolbar: [
                ['bold', 'italic', 'strike', 'blockquote'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['link'],
              ],
            }}
            formats={['bold', 'italic', 'strike', 'blockquote', 'list', 'bullet', 'link']}
            className={showToolbar ? 'show-toolbar' : ''}
            sx={{
              width: 'calc(100% - 80px)',
              marginBottom: 0,
              '& .ql-container': {
                border: 'none',
              },
            }}
          />
          {/* emoji button */}
          {!isMobile && (
            <InputAdornment
              position="end"
              sx={{ position: 'absolute', left: '-7px', bottom: '22px', display: 'flex', gap: '10px' }}
            >
              <IconButton onClick={handleEmojiPopover} edge="end">
                <EmojiEmotions />
              </IconButton>
            </InputAdornment>
          )}

          <InputAdornment
            position="end"
            sx={{ position: 'absolute', right: '15px', bottom: '22px', display: 'flex', gap: '10px' }}
          >
            {true ? null : (
              <>
                <input type="file" multiple hidden id="attachment-input" onChange={handleAttachmentChange} />
                <label htmlFor="attachment-input">
                  <Tooltip title="Attachments">
                    <IconButton component="span" sx={{ cursor: 'default', marginRight: '-5px' }}>
                      <Attachment />
                    </IconButton>
                  </Tooltip>
                </label>
              </>
            )}
            <IconButton
              onClick={() => {
                quillRef.current.getEditor().focus(); // Focus the Quill editor after submission
                setShowToolbar(!showToolbar);
              }}
              edge="end"
            >
              {showToolbar ? <KeyboardArrowDown /> : <EditNote />}
            </IconButton>
            <IconButton
              // type="submit"
              edge="end"
              onClick={
                () => {
                  quillRef.current.getEditor().focus();
                  openConfig();
                } // Focus the Quill editor after submission
              }
            >
              <Send />
            </IconButton>
          </InputAdornment>
          {editingAnnouncement ? (
            <>
              <InputAdornment
                position="end"
                sx={{
                  position: 'absolute',
                  left: '-3px',
                  top: '20px',
                  display: 'flex',
                  gap: '10px',
                }}
              >
                <IconButton
                  onClick={handleCancelEditing}
                  edge="end"
                  sx={{ background: 'lightgrey', borderRadius: '50%' }}
                >
                  <Close sx={{ fontSize: '12px', m: '-5px', color: 'black' }} />
                </IconButton>
              </InputAdornment>
              {/* <InputAdornment
              position="end"
              sx={{ position: 'absolute', left: '-10px', top: '20px', display: 'flex', gap: '10px' }}
            >
              <IconButton onClick={handleCancelEditing} edge="end">
              <Close fontSize="small" />
              </IconButton>
            </InputAdornment> */}
            </>
          ) : null}
        </QuillWrapper>
        <IconButton
          onClick={() => openConfig()}
          sx={{
            display: 'flex',
            position: 'relative',
            bottom: 4,
            ml: '5px',
            // right: '10px',
            borderRadius: '50%',
            border: 1,
            borderColor: 'divider',
            background: 'white',
            cursor: 'default',
            boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 3px, rgba(0, 0, 0, 0.20) 0px 1px 3px',
            p: 1,
          }}
        >
          <Tune sx={{ color: 'black' }} fontSize="small" />
        </IconButton>
        <Card
          sx={{
            background: formik.values?.attachments?.length > 0 ? 'white' : 'none',
            boxShadow: formik.values?.attachments?.length > 0 ? null : 'none',
            borderRadius: '20px',
            display: formik.values?.attachments?.length > 0 ? 'flex' : 'none',
            justifyContent: 'center',
            padding: '10px',
            gap: 3,
            zIndex: 101,
            position: 'absolute',
            top: '-72px',
            left: 0,
            // transform: 'translateX(-50%)',
          }}
        >
          <Box sx={{}}>
            {formik.values.attachments && formik.values.attachments.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'flex-end',
                  gap: 1,
                  flexWrap: 'nowrap',
                  cursor: 'default',
                }}
              >
                {filePreviews.map((preview, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mr: '10px', position: 'relative' }}>
                    <Tooltip title={formik.values.attachments[index].name} arrow>
                      <AttachAvatar
                        src={preview.url}
                        isVideoPreview={preview.type == 'video'}
                        publicCall={false}
                        variant="square"
                        sx={{ width: 50, height: 50 }}
                      />
                    </Tooltip>
                    <IconButton
                      sx={{
                        position: 'absolute',
                        right: '-15px',
                        top: '0%',
                        transform: 'translateY(-50%)',
                        background: 'lightgrey',
                        borderRadius: '50%',
                        cursor: 'default',
                      }}
                      onClick={() => handleRemoveAttachment(index)}
                    >
                      <Close sx={{ fontSize: '12px', m: '-5px', color: 'black' }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Card>
        <Popover
          id="emoji-picker-popover"
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <EmojiPicker onEmojiClick={handleEmojiClick} />
        </Popover>
      </form>
      <SwipeEdge
        height="60%"
        drawerBleeding={keepOpen && os !== 'ios' ? 0 : -0}
        title="Modify your pings"
        content={<SwipeContent />}
        // sx={{ zIndex: "1300 !important" }}
        // sx={{ position: 'relative', zIndex: '1300 !important' }}
      />
    </StyledPaper>
  );
};

export default AnnouncementForm;
